import { DateTime } from 'luxon';
import { bedLineForAccommodationBed } from '../../components/accommodation/AccommodationRoomBeds';
import { collectedByVendor } from '../../components/flight/breakdown-summary/VehicleCounterPayment';
import { capitalize, capitalizeWords, formatTerminal, humanize } from '../../components/flight/helpers/capitalize';
import { describeFlightRoute, formatFlightRouteLabel } from '../../components/flight/helpers/flightRouteLabel';
import { legTicketingIdentity } from '../../components/flight/helpers/legTicketingIdentity';
import { computeTripTitle } from '../../components/trip/computeTripTitle';
import { EnvConfig } from '../../config/configMapping';
import {
  AccommodationBedConfiguration,
  ChargeTripItemType,
  ChargeType,
  DefaultTripFragment,
  VehiclePaymentTiming,
} from '../../graphql/generated/graphql';
import { formatCurrency } from '../../helpers/formatCurrency';
import { formatLocalDateNoTz, formatLocalTime, formatTimeNoTz } from '../../helpers/formatTime';
import { formatTotalDuration } from '../../helpers/formatTotalDuration';
import { pluralize } from '../../helpers/pluralize';
import { ancillaryLines, sumChargedBreakdown } from '../../helpers/tripCharges';
import {
  buildProxyImageUrl,
  cancellationLine,
  getBaggageAllowanceText,
  getSeatSummary,
  getStopoverDurationBetweenSegments,
  htmlToPlainTextLines,
} from './helpers';
import {
  BookingPdfAccommodation,
  BookingPdfData,
  BookingPdfFlight,
  BookingPdfInstructionSection,
  BookingPdfPrice,
  BookingPdfPriceLine,
  BookingPdfSegment,
} from './types';

type TripAccommodation = NonNullable<DefaultTripFragment['tripAccommodations']>[number];
type AccommodationOffer = TripAccommodation['offer'];

// Adapt a DefaultTripFragment to the renderer's input contract.
// We pre-format everything here (dates, money, durations, terminal labels, HTML
// strip) so the renderer stays a pure presentation layer.

const computeTravelerName = (traveler: DefaultTripFragment['traveler']): string => {
  if (traveler?.firstName || traveler?.lastName) {
    return `${traveler?.firstName ?? ''} ${traveler?.lastName ?? ''}`.trim();
  }
  return traveler?.email ?? 'Unknown traveler';
};

const airportLabel = (endpoint: { name?: string | null; iataCode: string }): string => {
  const shortName = (endpoint.name ?? '').replace('International Airport', '').replace('Airport', '').trim();
  return shortName ? `${shortName} (${endpoint.iataCode})` : endpoint.iataCode;
};

const stopLabelFor = (segmentCount: number): string => {
  if (segmentCount <= 1) {
    return 'Direct';
  }
  return `${segmentCount - 1} stop${segmentCount - 1 > 1 ? 's' : ''}`;
};

// Build per-flight, per-itinerary entries; we treat each itinerary as one card.
const buildFlights = (trip: DefaultTripFragment): BookingPdfFlight[] => {
  const flights: BookingPdfFlight[] = [];

  // Show a visa reminder when any segment crosses an international border,
  // mirroring the email's hasInternationalFlight check.
  const hasInternationalFlight = (trip.tripFlights ?? []).some((tripFlight) =>
    tripFlight.offer.itineraries.some((itinerary) =>
      itinerary.segments.some(
        (segment) =>
          segment.departure?.country &&
          segment.arrival?.country &&
          segment.departure.country !== segment.arrival.country,
      ),
    ),
  );
  const internationalNote =
    'This trip includes international flight(s). Please ensure you have the required visa and travel documents for your destination(s) before departure.';

  for (const tripFlight of trip.tripFlights ?? []) {
    for (const itinerary of tripFlight.offer.itineraries) {
      const segments = itinerary.segments;
      if (segments.length === 0) {
        continue;
      }
      const route = describeFlightRoute([itinerary]);
      if (!route.destination) {
        continue;
      }
      const finalDestinationName = (route.destination.name ?? route.destination.iataCode)
        .replace('International Airport', '')
        .replace('Airport', '')
        .trim();

      const pdfSegments: BookingPdfSegment[] = [];
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        if (!segment) {
          continue;
        }
        const departure = segment.departure;
        const arrival = segment.arrival;
        if (!departure || !arrival) {
          continue;
        }
        const cabin = tripFlight.offer.travelers[0]?.detailsBySegment.find(
          (detail) => detail.segmentId === segment.id,
        )?.cabin;
        const next = segments[i + 1];
        const stopoverDuration = next ? getStopoverDurationBetweenSegments(segment, next) : undefined;

        pdfSegments.push({
          airlineName: segment.airline?.name ?? 'Airline',
          airlineCode: segment.airline?.iataCode ?? '—',
          flightNumber: segment.flightNumber,
          cabin: cabin ? capitalize(humanize(cabin)) : undefined,
          operatedBy: segment.operatedBy
            ? `${segment.operatedBy.name ?? ''} ${segment.operatedBy.iataCode}-${segment.operatedBy.flightNumber}`.trim()
            : undefined,
          departure: {
            date: formatLocalDateNoTz(departure.datetime),
            time: formatTimeNoTz(departure.datetime),
            city: airportLabel(departure),
            terminal: formatTerminal(departure.terminal),
          },
          arrival: {
            date: formatLocalDateNoTz(arrival.datetime),
            time: formatTimeNoTz(arrival.datetime),
            city: airportLabel(arrival),
            terminal: formatTerminal(arrival.terminal),
          },
          duration: formatTotalDuration(segment.durationInSeconds),
          stopoverDuration: stopoverDuration || undefined,
        });
      }

      if (pdfSegments.length === 0) {
        continue;
      }

      // A row can hold one leg of a multi-carrier PNR, where the booking-level value names every carrier's
      // locator rather than the one flying this leg. Read the leg's own segments instead.
      const { airlineConfirmationId, eTicketNumber } = legTicketingIdentity(segments, {
        sourceId: tripFlight.sourceId ?? null,
        airlineConfirmationId: tripFlight.airlineConfirmationId,
        eTicketNumber: tripFlight.eTicketNumber,
      });

      const fare = itinerary.fares[0];
      const baggageText = fare?.baggage ? getBaggageAllowanceText(fare.baggage) : undefined;
      const seatSummary = getSeatSummary(itinerary, tripFlight.offerExtraInformation?.travelerSelections);

      flights.push({
        title: formatFlightRouteLabel(route.kind, finalDestinationName),
        bookingRef: tripFlight.sourceId ?? undefined,
        note: hasInternationalFlight ? internationalNote : undefined,
        segments: pdfSegments,
        stopLabel: stopLabelFor(segments.length),
        seatSummary: seatSummary || undefined,
        airlineConfirmationId: airlineConfirmationId ?? undefined,
        eTicket: eTicketNumber ?? undefined,
        baggage: baggageText && baggageText !== 'Information not specified' ? baggageText : undefined,
      });
    }
  }
  return flights;
};

// Address line for the hotel card. Matches the email's formatAddress.
const formatAddress = (offer: AccommodationOffer): string => {
  const parts = [
    offer.address?.lines?.filter(Boolean).join(', '),
    offer.address?.cityName,
    offer.address?.stateName,
    offer.address?.postalCode,
  ].filter(Boolean);
  return parts.join(', ');
};

// Build the Google Maps deep-link the email uses; we surface the URL as a
// muted line under the address since pdf-lib annotation links aren't widely
// supported in viewers.
const buildGoogleMapsUrl = (offer: AccommodationOffer): string => {
  const addressString = [
    offer.address?.lines?.join(', '),
    offer.address?.cityName,
    offer.address?.stateName,
    offer.address?.postalCode,
    offer.address?.countryCode,
  ]
    .filter(Boolean)
    .join(', ');
  const coords = offer.address?.coordinates;
  const query =
    offer.name && offer.name.trim().length > 0
      ? `${offer.name}, ${addressString}`
      : coords
        ? `${coords.latitude},${coords.longitude}`
        : addressString;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

const buildInstructionSections = (offer: AccommodationOffer): BookingPdfInstructionSection[] => {
  const sections: BookingPdfInstructionSection[] = [];

  const pushFromHtml = (title: string, html: string | null | undefined) => {
    const lines = htmlToPlainTextLines(html);
    if (lines.length > 0) {
      sections.push({ title, lines });
    }
  };

  const checkInLines = [
    ...htmlToPlainTextLines(offer.checkIn?.instructions),
    ...htmlToPlainTextLines(offer.checkIn?.specialInstructions),
  ];
  if (checkInLines.length > 0) {
    sections.push({ title: 'Check-in instructions', lines: checkInLines });
  }
  const checkOutLines = [
    ...htmlToPlainTextLines(offer.checkOut?.instructions),
    ...htmlToPlainTextLines(offer.checkOut?.specialInstructions),
  ];
  if (checkOutLines.length > 0) {
    sections.push({ title: 'Check-out instructions', lines: checkOutLines });
  }
  pushFromHtml('Mandatory fees', offer.policies?.mandatoryFees);
  pushFromHtml('Optional fees', offer.policies?.optionalFees);
  pushFromHtml('Traveler service fees', offer.policies?.travelerServiceFees);

  return sections;
};

const fetchHeroImageBytes = async (
  envConfig: EnvConfig,
  rawUrl: string | undefined,
): Promise<{ bytes: Uint8Array; ext: 'jpg' | 'png' } | undefined> => {
  if (!rawUrl) {
    return undefined;
  }
  try {
    const { url, ext } = buildProxyImageUrl(envConfig.backendUrl, rawUrl);
    const response = await fetch(url);
    if (!response.ok) {
      return undefined;
    }
    const buffer = await response.arrayBuffer();
    return { bytes: new Uint8Array(buffer), ext };
  } catch {
    return undefined;
  }
};

// Collect every unique airline IATA code on the trip and resolve each one's
// logo bytes in parallel. The PNGs come from the public assets bucket served
// at envConfig.assetsPublicUrl/airlines/symbol/<code>.png — same path the
// email uses — and are routed through the backend image proxy so the bytes
// land same-origin and bypass supplier CORS.
const fetchAirlineLogos = async (
  trip: DefaultTripFragment,
  envConfig: EnvConfig,
): Promise<Record<string, { bytes: Uint8Array; ext: 'jpg' | 'png' }>> => {
  const codes = new Set<string>();
  for (const tripFlight of trip.tripFlights ?? []) {
    for (const itinerary of tripFlight.offer.itineraries) {
      for (const segment of itinerary.segments) {
        if (segment.airline?.iataCode) {
          codes.add(segment.airline.iataCode);
        }
      }
    }
  }
  const entries = await Promise.all(
    Array.from(codes).map(async (code) => {
      const fetched = await fetchHeroImageBytes(envConfig, `${envConfig.assetsPublicUrl}/airlines/symbol/${code}.png`);
      return fetched ? ([code, fetched] as const) : null;
    }),
  );
  return Object.fromEntries(entries.filter((entry): entry is NonNullable<typeof entry> => Boolean(entry)));
};

const buildAccommodations = async (
  trip: DefaultTripFragment,
  envConfig: EnvConfig,
): Promise<BookingPdfAccommodation[]> => {
  const accommodations = trip.tripAccommodations ?? [];
  return Promise.all(
    accommodations.map(async (tripAccommodation): Promise<BookingPdfAccommodation> => {
      const offer = tripAccommodation.offer;
      const room = offer.rooms[0];
      const rate = room?.rates[0];

      const bedConfigurations = (rate?.bedConfigurations ?? []) as AccommodationBedConfiguration[];
      const bed =
        bedConfigurations.length > 0
          ? bedLineForAccommodationBed(bedConfigurations[0].beds)
          : room?.beds && room.beds.length > 0
            ? bedLineForAccommodationBed(room.beds)
            : '';

      const rateAmenities = rate?.amenities?.map((a) => a.name).filter(Boolean) ?? [];
      const cancellation = rate?.cancellationPolicies ? cancellationLine(rate.cancellationPolicies) : undefined;

      const heroImage = await fetchHeroImageBytes(envConfig, offer.photos?.[0]?.medium?.url);

      return {
        name: offer.name?.trim() || offer.brand?.brandName || 'Accommodation',
        address: formatAddress(offer) || undefined,
        mapsUrl: buildGoogleMapsUrl(offer),
        checkIn: {
          date: DateTime.fromISO(offer.checkIn.date).toFormat('dd LLL yyyy'),
          sub: offer.checkIn.fromTime ? `From ${formatLocalTime(offer.checkIn.fromTime)}` : undefined,
        },
        checkOut: {
          date: DateTime.fromISO(offer.checkOut.date).toFormat('dd LLL yyyy'),
          sub: offer.checkOut.toTime ? `By ${formatLocalTime(offer.checkOut.toTime)}` : undefined,
        },
        room: room?.name ? capitalize(room.name) : undefined,
        bed: bed || undefined,
        bedNotGuaranteed: rate?.bedIsGuaranteed === false,
        included: rateAmenities.length > 0 ? rateAmenities.map((a) => capitalize(a)).join(', ') : undefined,
        bookingId: tripAccommodation.sourceId ?? undefined,
        cancellation: offer.sourceMetadata?.name === 'manual' ? undefined : cancellation,
        instructionSections: buildInstructionSections(offer),
        heroImage,
      };
    }),
  );
};

// Build price summary in the same shape as the email's PriceSummary:
// per-flight grand total + taxes/fees sub, per-hotel nights line + taxes/fees
// sub, booking fee, optional card surcharge, total, charge line, disclaimer.
const buildPrice = (trip: DefaultTripFragment): BookingPdfPrice | undefined => {
  const flights = trip.tripFlights ?? [];
  const accommodations = trip.tripAccommodations ?? [];
  const vehicles = trip.tripVehicles ?? [];
  if (flights.length === 0 && accommodations.length === 0 && vehicles.length === 0) {
    return undefined;
  }

  const currency =
    flights[0]?.offer?.price?.currency ??
    accommodations[0]?.offer?.rooms?.[0]?.rates?.[0]?.price?.currency ??
    trip.currency ??
    'AUD';

  const lines: BookingPdfPriceLine[] = [];

  for (const tripFlight of flights) {
    const price = tripFlight.offer.price;
    if (!price) {
      continue;
    }
    const itinerary = tripFlight.offer.itineraries[0];
    const firstSegment = itinerary?.segments[0];
    const lastSegment = itinerary?.segments[itinerary.segments.length - 1];
    const origin = firstSegment?.departure?.city ?? firstSegment?.departure?.name ?? '';
    const destination = lastSegment?.arrival?.city ?? lastSegment?.arrival?.name ?? '';
    lines.push({
      label: `Flight: ${origin} to ${destination}`,
      value: formatCurrency(price.grandTotal, price.currency),
    });
  }

  for (const tripAccommodation of accommodations) {
    const offer = tripAccommodation.offer;
    const rate = offer.rooms[0]?.rates[0];
    if (!rate?.price) {
      continue;
    }
    lines.push({
      label: `Hotel: ${offer.numberOfNights} ${pluralize('night', offer.numberOfNights)}`,
      value: formatCurrency(rate.price.grandTotal, rate.price.currency),
    });
  }

  // Every car has its line and counts in the total, like a stay. A car paid at the counter adds what is due at pick up
  // at the vendor under its line, since the vendor collects it and we never charge it.
  const disclaimers: string[] = [];
  for (const tripVehicle of vehicles) {
    const { price, vendor } = tripVehicle.offer;
    lines.push({
      label: `Car: ${price.rentalDays} ${pluralize('day', price.rentalDays)}`,
      value: formatCurrency(price.total, price.currency),
    });
    if (tripVehicle.paymentTiming === VehiclePaymentTiming.Prepaid) {
      continue;
    }

    const vendorTotal = price.vendorTotal;
    lines.push({
      label: `Due at pick up at ${vendor.name}`,
      sub: true,
      value: vendorTotal
        ? `${vendorTotal.currency} ${formatCurrency(vendorTotal.amount, vendorTotal.currency, vendorTotal.currency)}`
        : `${price.currency} ${formatCurrency(price.total, price.currency, price.currency)}`,
    });
    if (vendorTotal) {
      lines.push({
        label: 'Approximately*',
        value: `${price.currency} ${formatCurrency(price.total, price.currency, price.currency)}`,
        sub: true,
      });
    }
    disclaimers.push(collectedByVendor(vendor.name));
    if (vendorTotal) {
      disclaimers.push(
        '*This conversion is an approximate price based on the current exchange rate and may change before your pick up.',
      );
    }
  }

  // Extra charges — keep the email's order (ancillaries, then booking fee always, then surcharge
  // only when present). Read from the charge records rather than `Trip.extraCharges`: nothing ever
  // writes that column, so the fee always printed as $0.00 and the surcharge never appeared, while
  // the Total below comes from `trip.totalAmount` (which does count them) and so disagreed.
  const ancillaries = ancillaryLines(trip.tripChargeRecords);
  const bookingFee = sumChargedBreakdown(trip.tripChargeRecords, ChargeTripItemType.BookingFee);
  const ccSurcharge = sumChargedBreakdown(trip.tripChargeRecords, ChargeTripItemType.CreditCardSurcharge);

  for (const ancillary of ancillaries) {
    lines.push({ label: ancillary.label, value: formatCurrency(ancillary.amount, currency) });
  }
  lines.push({ label: 'Booking fee', value: formatCurrency(bookingFee, currency) });
  if (ccSurcharge > 0) {
    lines.push({ label: 'Card surcharge', value: formatCurrency(ccSurcharge, currency) });
  }

  // Charge record (when present, surfaces payment-method tail + charge date).
  const chargeRecord = (trip.tripChargeRecords ?? []).find(
    (record) => record.type === ChargeType.Charge && record.paymentMethod,
  );
  const charge = chargeRecord?.paymentMethod
    ? {
        description: `Charged to ${chargeRecord.paymentMethod.brand} ···· ${chargeRecord.paymentMethod.last4}`,
        date: chargeRecord.createdAt
          ? DateTime.fromISO(
              typeof chargeRecord.createdAt === 'string'
                ? chargeRecord.createdAt
                : (chargeRecord.createdAt.toISO() ?? ''),
            ).toFormat('dd LLL yyyy')
          : undefined,
      }
    : undefined;

  return {
    lines,
    // The trip total: the charged total plus what the vendors collect at the desk, so the Car lines and the total agree.
    total: { label: 'Trip total', value: formatCurrency(trip.tripTotal, currency) },
    charge,
    disclaimer: disclaimers.length > 0 ? [...new Set(disclaimers)].join(' ') : undefined,
  };
};

// Manual booking flag — when any flight or accommodation comes from a manual
// source, the email substitutes a cancellation-policy notice for the price
// summary. We do the same.
const isManualBooking = (trip: DefaultTripFragment): boolean => {
  const flights = trip.tripFlights ?? [];
  const accommodations = trip.tripAccommodations ?? [];
  return (
    flights.some((flight) => flight.offer.sourceMetadata?.name === 'manual') ||
    accommodations.some((accommodation) => accommodation.offer.sourceMetadata?.name === 'manual')
  );
};

// Build the renderer input from a trip fragment.
export async function tripToBookingPdfData(trip: DefaultTripFragment, envConfig: EnvConfig): Promise<BookingPdfData> {
  const flights = buildFlights(trip);
  // Hotel photos + airline logos resolve in parallel — both go through the
  // backend image proxy so the bytes land on the same origin as the SPA.
  const [accommodations, airlineLogos] = await Promise.all([
    buildAccommodations(trip, envConfig),
    fetchAirlineLogos(trip, envConfig),
  ]);
  const firstFlightSegment = trip.tripFlights?.[0]?.offer?.itineraries?.[0]?.segments?.[0];
  // The backend-resolved wall-clock day is the single source for trip dates, with the segment's own
  // wall clock as the fallback for payloads fetched without `startDate`. `startAt` was a third
  // fallback and is gone: it is an instant, so formatting it here printed a day that moved with the
  // reader. Both remaining sources are the clock at the departure airport.
  const tripDateSource = trip.startDate ?? firstFlightSegment?.departure?.datetime;

  const manual = isManualBooking(trip);
  const manualNotice = manual
    ? 'Cancellations are subject to the suppliers terms and conditions. For assistance with cancelling or changing your trip details please reach out to our team using the contact details below.'
    : undefined;

  // Reuse the same word-flow shape as the email: capitalize human-readable labels.
  void capitalizeWords;

  return {
    title: computeTripTitle(
      {
        flight: trip.tripFlights?.[0]?.offer,
        accommodation: trip.tripAccommodations?.[0]?.offer,
        vehicle: trip.tripVehicles?.[0]?.offer,
      },
      { destinationCity: trip.destinationCity, startDate: trip.startDate },
    ),
    tripDate: tripDateSource ? formatLocalDateNoTz(tripDateSource) : undefined,
    bookingId: trip.bookingId ?? undefined,
    traveler: {
      name: computeTravelerName(trip.traveler),
      email: trip.traveler?.email ?? 'Unknown email',
    },
    flights,
    accommodations,
    airlineLogos,
    price: manual ? undefined : buildPrice(trip),
    manualBookingNotice: manualNotice,
  };
}
